import { createSelector } from "@reduxjs/toolkit";
import {
  getDeeplyNestedValue,
  getInstanceClassZIndex,
  type IMcBimItem,
} from "@multiconsult-group/multiplacer-core";
import type { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { buildCeilingGridPixPathsFromLines } from "../buildCeilingGridPixPathsFromLines";
import { convertMcBimSpaceToPixPolygon } from "../DesignTab/pixpolygonSelectors";
import {
  convertMcBimItemToPixPolygons,
  getMcBimItemDisplayColor,
} from "../DesignTab/transformMCBimItemsToPolygons";
import type { IIsoViewerRenderItem } from "../RoomSectionViews/roomSectionViewSelectors";
import type { IViewerSectionCenterCoordinate } from "../ViewerRedux/viewerSlice";
import type { PartialMcBimSpace } from "../utils";
import type { IRootState } from "../app/store";
import { selectCompCodePath } from "../globalSelectors";
import type {
  GetMcbimItemsInSpaceApiArg,
  GetMcbimItemsInSpaceApiResponse,
  GetMcbimSingleMcbimSpaceApiArg,
} from "../../openapi";
import { openapi } from "../../openapi";
import { selectDrofusOccurrenceRoomViewerState } from "./drofusForekomsterSlice";
import { selectClickedMcBimItemId } from "../ViewerRedux/viewerSlice";

type McBimItemInSpace = GetMcbimItemsInSpaceApiResponse[number];
const EMPTY_MCBIM_ITEMS: GetMcbimItemsInSpaceApiResponse = [];

export type DrofusOccurrenceRoomViewerSelectorInput = {
  space?: PartialMcBimSpace | null;
  items: McBimItemInSpace[];
  compCodePath?: string | null;
  articleCompCode?: string | null;
  articleToBeDrawn?: boolean | null;
  showLengthComponents: boolean;
  clickedMcBimItemId?: string | null;
  isFetching: boolean;
};

export type DrofusOccurrenceRoomViewerModel = {
  polygons: IPixPolygon[];
  ceilingGridPaths: ReturnType<typeof buildCeilingGridPixPathsFromLines>;
  sectionRenderItems: IIsoViewerRenderItem[];
  sectionCenterCoordinate: IViewerSectionCenterCoordinate | null;
  normalizedArticleCompCode: string | null;
  matchCount: number;
  visibleItemCount: number;
  hiddenLengthItemCount: number;
  showMissingModeledMatchWarning: boolean;
  zoomExtentKey: string;
};

export type DrofusOccurrenceRoomViewerCanvasModel = Pick<
  DrofusOccurrenceRoomViewerModel,
  "polygons" | "ceilingGridPaths" | "zoomExtentKey"
> & {
  rotation: number;
  isFetchingItems: boolean;
};

const selectInput = (input: DrofusOccurrenceRoomViewerSelectorInput) => input;

export function normalizeDrofusOccurrenceCompCode(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (value && typeof value === "object" && "value" in value) {
    return normalizeDrofusOccurrenceCompCode((value as { value?: unknown }).value);
  }

  return null;
}

function getItemCompCode(item: McBimItemInSpace, compCodePath: string): string | null {
  return normalizeDrofusOccurrenceCompCode(getDeeplyNestedValue(item, compCodePath));
}

function isLengthComponent(item: McBimItemInSpace): boolean {
  const instanceClass = item.instanceClass ?? "";
  const typeClass = item.type?.typeClass ?? "";

  return (
    instanceClass.includes("Segment") ||
    instanceClass.includes("Fitting") ||
    typeClass.includes("Segment") ||
    typeClass.includes("Fitting") ||
    instanceClass === "IfcCableCarrierSegment" ||
    instanceClass === "IfcCableSegment" ||
    instanceClass === "IfcDuctSegment" ||
    instanceClass === "IfcPipeSegment" ||
    instanceClass === "IfcCableCarrierFitting" ||
    instanceClass === "IfcCableFitting" ||
    instanceClass === "IfcDuctFitting" ||
    instanceClass === "IfcPipeFitting" ||
    instanceClass === "IfcFlowFitting"
  );
}

function isSpaceItem(item: McBimItemInSpace): boolean {
  return item.instanceClass === "IfcSpace" || item.type?.typeClass === "IfcSpaceType";
}

function isVisibleRoomItem(item: McBimItemInSpace, showLengthComponents: boolean): boolean {
  return !isSpaceItem(item) && (showLengthComponents || !isLengthComponent(item));
}

function getItemDrawHeight(item: McBimItemInSpace): number {
  const relMiddle = item.geometry?.zRelM?.middle;
  if (Number.isFinite(relMiddle)) return relMiddle as number;

  const relBottom = item.geometry?.zRelM?.bottom;
  if (Number.isFinite(relBottom)) return relBottom as number;

  const worldMiddle = item.geometry?.zWorldM?.middle;
  if (Number.isFinite(worldMiddle)) return worldMiddle as number;

  const worldBottom = item.geometry?.zWorldM?.bottom;
  if (Number.isFinite(worldBottom)) return worldBottom as number;

  const centerZ = item.centerPoint?.[2];
  if (Number.isFinite(centerZ)) return centerZ as number;

  const obbCenterZ = item.geometry?.Obb3DWorldMmList?.[0]?.centerM?.z;
  if (Number.isFinite(obbCenterZ)) return obbCenterZ as number;

  return Number.NEGATIVE_INFINITY;
}

function sortItemsByHeightForDrawing(items: McBimItemInSpace[]): McBimItemInSpace[] {
  return items
    .map((item, index) => ({
      item,
      index,
      height: getItemDrawHeight(item),
      classOrder: getInstanceClassZIndex(item.instanceClass ?? ""),
    }))
    .sort((a, b) => {
      if (a.height !== b.height) return a.height - b.height;
      if (a.classOrder !== b.classOrder) return a.classOrder - b.classOrder;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

function getPolygonCenterCoordinate(
  points: number[][] | null | undefined
): IViewerSectionCenterCoordinate | null {
  if (!points || points.length === 0) {
    return null;
  }

  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);

  return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
}

function getSectionCenterCoordinate(space: unknown): IViewerSectionCenterCoordinate | null {
  const geometry = (space as { geometry?: { polygon2d?: number[][]; bbox2d?: number[][] } } | null)
    ?.geometry;

  return (
    getPolygonCenterCoordinate(geometry?.polygon2d) ?? getPolygonCenterCoordinate(geometry?.bbox2d)
  );
}

function buildRoomPolygon(space: PartialMcBimSpace | null | undefined): IPixPolygon | null {
  if (!space) {
    return null;
  }

  const roomPolygon = convertMcBimSpaceToPixPolygon({
    mcBimSpace: { ...space } as PartialMcBimSpace,
    dragSelectSpaces: false,
    showText: false,
  });

  if (!roomPolygon) {
    return null;
  }

  return {
    ...roomPolygon,
    fillColor: undefined,
    strokeColor: "#212529",
    strokeWidth: "1",
  };
}

function buildItemPolygons(args: {
  items: McBimItemInSpace[];
  compCodePath: string;
  normalizedArticleCompCode: string | null;
  showLengthComponents: boolean;
}): { polygons: IPixPolygon[]; matchCount: number } {
  let matchCount = 0;
  const polygons: IPixPolygon[] = [];

  for (const item of sortItemsByHeightForDrawing(args.items)) {
    if (!isVisibleRoomItem(item, args.showLengthComponents)) {
      continue;
    }

    const itemCompCode = getItemCompCode(item, args.compCodePath);
    const polygon = convertMcBimItemToPixPolygons({
      mcBimItem: item as Parameters<typeof convertMcBimItemToPixPolygons>[0]["mcBimItem"],
      mcbimItemColorMode: "MULTIPLACER_COLORS",
    });

    const isMatch =
      args.normalizedArticleCompCode != null && itemCompCode === args.normalizedArticleCompCode;

    if (!polygon) {
      continue;
    }

    if (!isMatch) {
      polygons.push(polygon);
      continue;
    }

    matchCount += 1;

    polygons.push({
      ...polygon,
      fillColor: "#fff3bf",
      strokeColor: "#f08c00",
      strokeWidth: "3",
      text: {
        ...polygon.text,
        values: [itemCompCode ?? args.normalizedArticleCompCode],
        color: "#000000",
        show: true,
      },
    });
  }

  return { polygons, matchCount };
}

function buildSectionRenderItems(args: {
  items: McBimItemInSpace[];
  compCodePath: string | null | undefined;
  normalizedArticleCompCode: string | null;
  showLengthComponents: boolean;
  clickedMcBimItemId?: string | null;
}): IIsoViewerRenderItem[] {
  if (!args.compCodePath) {
    return [];
  }

  return args.items
    .filter((item) => isVisibleRoomItem(item, args.showLengthComponents))
    .map((item) => {
      const itemCompCode = getItemCompCode(item, args.compCodePath as string);
      const isMatch =
        args.normalizedArticleCompCode != null && itemCompCode === args.normalizedArticleCompCode;
      const isClicked =
        args.clickedMcBimItemId != null &&
        item._id != null &&
        String(item._id) === args.clickedMcBimItemId;

      return {
        item: item as IMcBimItem,
        color: isMatch
          ? "#f08c00"
          : (getMcBimItemDisplayColor({
              mcbimItemColorMode: "MULTIPLACER_COLORS",
              item: item as IMcBimItem,
            }) ?? "#74c0fc"),
        opacity: isMatch || isClicked ? 1 : 0.86,
        outline: isClicked
          ? { color: "#ff0000", width: 4 }
          : isMatch
            ? { color: "#ff0000", width: 3 }
            : undefined,
      };
    });
}

export const selectDrofusOccurrenceRoomViewerModel = createSelector(
  [selectInput],
  ({
    space,
    items,
    compCodePath,
    articleCompCode,
    articleToBeDrawn,
    showLengthComponents,
    clickedMcBimItemId,
    isFetching,
  }): DrofusOccurrenceRoomViewerModel => {
    const normalizedArticleCompCode = normalizeDrofusOccurrenceCompCode(articleCompCode);
    const visibleItemCount = showLengthComponents
      ? items.length
      : items.filter((item) => !isLengthComponent(item)).length;
    const hiddenLengthItemCount = items.length - visibleItemCount;

    const roomPolygon = buildRoomPolygon(space);
    const itemPolygons = compCodePath
      ? buildItemPolygons({
          items,
          compCodePath,
          normalizedArticleCompCode,
          showLengthComponents,
        })
      : { polygons: [], matchCount: 0 };

    const polygons = roomPolygon ? [roomPolygon, ...itemPolygons.polygons] : itemPolygons.polygons;
    const matchCount = itemPolygons.matchCount;
    const spaceName = (space as { name?: string } | null | undefined)?.name;

    return {
      polygons,
      ceilingGridPaths: buildCeilingGridPixPathsFromLines(space ? [space] : []),
      sectionRenderItems: buildSectionRenderItems({
        items,
        compCodePath,
        normalizedArticleCompCode,
        showLengthComponents,
        clickedMcBimItemId,
      }),
      sectionCenterCoordinate: getSectionCenterCoordinate(space),
      normalizedArticleCompCode,
      matchCount,
      visibleItemCount,
      hiddenLengthItemCount,
      showMissingModeledMatchWarning: articleToBeDrawn === true && !isFetching && matchCount === 0,
      zoomExtentKey: spaceName
        ? `drofus-occurrence-room-${spaceName}-${visibleItemCount}-${matchCount}-${showLengthComponents}`
        : "drofus-occurrence-room-empty",
    };
  }
);

export const selectDrofusOccurrenceRoomViewerSpaceApiArg = createSelector(
  selectDrofusOccurrenceRoomViewerState,
  (roomViewer): GetMcbimSingleMcbimSpaceApiArg | null => {
    if (!roomViewer.roomFunctionNo || !roomViewer.projectNo) return null;

    return {
      projectNo: roomViewer.projectNo,
      name: roomViewer.roomFunctionNo,
    };
  }
);

export const selectDrofusOccurrenceRoomViewerItemsApiArg = createSelector(
  selectDrofusOccurrenceRoomViewerState,
  (roomViewer): GetMcbimItemsInSpaceApiArg | null => {
    if (!roomViewer.roomFunctionNo || !roomViewer.projectNo) return null;

    return {
      projectNo: roomViewer.projectNo,
      spaceName: roomViewer.roomFunctionNo,
    };
  }
);

const selectDrofusOccurrenceRoomViewerSpace = createSelector(
  [selectDrofusOccurrenceRoomViewerSpaceApiArg, (state: IRootState) => state],
  (args, state) => {
    if (!args) return null;
    return openapi.endpoints.getMcbimSingleMcbimSpace.select(args)(state).data ?? null;
  }
);

const selectDrofusOccurrenceRoomViewerItems = createSelector(
  [selectDrofusOccurrenceRoomViewerItemsApiArg, (state: IRootState) => state],
  (args, state): GetMcbimItemsInSpaceApiResponse => {
    if (!args) return EMPTY_MCBIM_ITEMS;
    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? EMPTY_MCBIM_ITEMS;
  }
);

const selectDrofusOccurrenceRoomViewerSpaceIsFetching = createSelector(
  [selectDrofusOccurrenceRoomViewerSpaceApiArg, (state: IRootState) => state],
  (args, state): boolean => {
    if (!args) return false;
    return openapi.endpoints.getMcbimSingleMcbimSpace.select(args)(state).isLoading === true;
  }
);

export const selectDrofusOccurrenceRoomViewerItemsIsFetching = createSelector(
  [selectDrofusOccurrenceRoomViewerItemsApiArg, (state: IRootState) => state],
  (args, state): boolean => {
    if (!args) return false;
    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).isLoading === true;
  }
);

export const selectDrofusOccurrenceRoomViewerModelFromRedux = createSelector(
  [
    selectDrofusOccurrenceRoomViewerSpace,
    selectDrofusOccurrenceRoomViewerItems,
    selectCompCodePath,
    selectDrofusOccurrenceRoomViewerState,
    selectClickedMcBimItemId,
    selectDrofusOccurrenceRoomViewerSpaceIsFetching,
    selectDrofusOccurrenceRoomViewerItemsIsFetching,
  ],
  (
    space,
    items,
    compCodePath,
    roomViewer,
    clickedMcBimItemId,
    isFetchingSpace,
    isFetchingItems
  ): DrofusOccurrenceRoomViewerModel =>
    selectDrofusOccurrenceRoomViewerModel({
      space: space as PartialMcBimSpace | null | undefined,
      items,
      compCodePath,
      articleCompCode: roomViewer.articleCompCode,
      articleToBeDrawn: roomViewer.articleToBeDrawn,
      showLengthComponents: roomViewer.showLengthComponents,
      clickedMcBimItemId,
      isFetching: isFetchingSpace || isFetchingItems,
    })
);

export const selectDrofusOccurrenceRoomViewerCanvasModel = createSelector(
  [
    selectDrofusOccurrenceRoomViewerModelFromRedux,
    (state: IRootState) => state.design.rotation,
    selectDrofusOccurrenceRoomViewerItemsIsFetching,
  ],
  (viewerModel, rotation, isFetchingItems): DrofusOccurrenceRoomViewerCanvasModel => ({
    polygons: viewerModel.polygons,
    ceilingGridPaths: viewerModel.ceilingGridPaths,
    zoomExtentKey: viewerModel.zoomExtentKey,
    rotation,
    isFetchingItems,
  })
);
